import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import * as Lucide from "lucide-react";
import {
  IdCard, BookOpen, Briefcase, FileText,
  ShieldCheck, Clock, IndianRupee, MessageCircle, ArrowRight, Loader2, ListChecks,
} from "lucide-react";
import OrderNowModal from "./OrderNowModal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const WHATSAPP_NUMBER = (process.env.REACT_APP_WHATSAPP_NUMBER || "").replace(/\D/g, "");

/** Map catalog `icon` string → lucide component, safe fallback. */
function iconFor(name) {
  const KNOWN = { IdCard, BookOpen, Briefcase, FileText };
  return KNOWN[name] || Lucide[name] || FileText;
}

function inrDisplay(paise) {
  const inr = Math.round((paise || 0) / 100);
  return `₹${inr.toLocaleString("en-IN")}`;
}

function waLink(service) {
  const base = WHATSAPP_NUMBER || "918055592955";
  const parts = [
    `Hello UID Infotech, I want to apply for *${service.name}*.`,
    "",
    "Here are my documents:",
    ...(service.required_documents || []).map((d, i) => `${i + 1}. ${d} — [attach]`),
    "",
    `Reference price: ${inrDisplay(service.price_paise)}  ·  Expected turnaround: ${service.turnaround_hours}h`,
  ];
  return `https://wa.me/${base}?text=${encodeURIComponent(parts.join("\n"))}`;
}

function ServiceCard({ svc }) {
  const { t } = useTranslation();
  const Icon = iconFor(svc.icon);
  const [orderOpen, setOrderOpen] = useState(false);
  return (
    <article
      className="group relative flex flex-col gap-4 rounded-2xl border border-white/10 bg-white/[0.03] backdrop-blur-md p-6 hover:border-emerald-400/40 hover:bg-white/[0.05] transition-all"
      data-testid={`formhub-card-${svc.slug}`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="grid h-12 w-12 place-items-center rounded-xl bg-gradient-to-br from-emerald-500/25 to-teal-400/15 border border-white/10 group-hover:border-emerald-400/40 transition-colors">
          <Icon className="h-5 w-5 text-emerald-300" />
        </div>
        <div className="text-right">
          <div className="inline-flex items-center gap-1 rounded-full bg-emerald-500/10 border border-emerald-400/30 px-2.5 py-1 text-[11px] font-extrabold text-emerald-300">
            <IndianRupee className="h-3 w-3" />
            {inrDisplay(svc.price_paise).replace("₹", "")}
          </div>
          <div className="mt-1 inline-flex items-center gap-1 text-[10px] text-white/50">
            <Clock className="h-3 w-3" />
            <span className="font-semibold">{svc.turnaround_hours}{t("form_hub.turnaround_hours_suffix")}</span>
          </div>
        </div>
      </div>

      <div>
        <h3 className="text-base sm:text-lg font-bold text-white leading-tight group-hover:text-emerald-200 transition-colors">
          {svc.name}
        </h3>
        <p className="text-[13px] text-white/60 mt-2 leading-relaxed">
          {svc.short_description}
        </p>
      </div>

      {svc.required_documents?.length ? (
        <div className="rounded-xl border border-white/5 bg-black/25 px-3.5 py-3">
          <div className="flex items-center gap-1.5 mb-2 text-[10px] font-extrabold uppercase tracking-wider text-white/50">
            <ListChecks className="h-3 w-3" /> {t("form_hub.docs_needed")}
          </div>
          <ul className="space-y-1">
            {svc.required_documents.slice(0, 4).map((d, i) => (
              <li key={`${svc.slug}-doc-${i}`} className="flex items-start gap-1.5 text-[12px] text-white/70 leading-snug">
                <span className="mt-1.5 h-1 w-1 shrink-0 rounded-full bg-emerald-400/70" />
                <span>{d}</span>
              </li>
            ))}
            {svc.required_documents.length > 4 && (
              <li className="text-[11px] text-white/40 pl-3">+ {svc.required_documents.length - 4} {t("form_hub.more_docs_suffix")}</li>
            )}
          </ul>
        </div>
      ) : null}

      <div className="mt-auto grid grid-cols-1 sm:grid-cols-2 gap-2">
        <button
          type="button"
          onClick={() => setOrderOpen(true)}
          className="inline-flex items-center justify-center gap-1.5 rounded-full bg-emerald-500 hover:bg-emerald-600 px-3 py-2.5 text-sm font-bold text-white shadow-[0_8px_24px_-8px_rgba(16,185,129,0.6)] transition-all"
          data-testid={`formhub-order-${svc.slug}`}
        >
          <IndianRupee className="h-3.5 w-3.5" /> {t("form_hub.order_now")}
        </button>
        <a
          href={waLink(svc)}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center justify-center gap-1.5 rounded-full bg-[#25D366]/15 border border-[#25D366]/40 hover:bg-[#25D366]/25 px-3 py-2.5 text-sm font-bold text-emerald-200 transition-all"
          data-testid={`formhub-wa-${svc.slug}`}
        >
          <MessageCircle className="h-3.5 w-3.5" /> WhatsApp
        </a>
      </div>
      <OrderNowModal open={orderOpen} onClose={() => setOrderOpen(false)} service={svc} />
    </article>
  );
}

function SkeletonCard() {
  return (
    <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-6 animate-pulse space-y-4" data-testid="formhub-skeleton">
      <div className="h-12 w-12 rounded-xl bg-white/10" />
      <div className="h-4 w-3/4 rounded bg-white/10" />
      <div className="h-3 w-full rounded bg-white/5" />
      <div className="h-24 rounded-xl bg-white/5" />
      <div className="h-10 rounded-full bg-white/10" />
    </div>
  );
}

export default function FormHubSection() {
  const { t } = useTranslation();
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let alive = true;
    fetch(`${API}/form-hub/services`)
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))))
      .then((d) => {
        if (!alive) return;
        setServices(d.items || []);
        setLoading(false);
      })
      .catch((e) => {
        if (!alive) return;
        setError(e.message);
        setLoading(false);
      });
    return () => { alive = false; };
  }, []);

  return (
    <section
      id="form-hub"
      className="section-pad border-t border-white/5 bg-gradient-to-b from-[#0A101D] via-[#0B1220] to-[#0A101D]"
      data-testid="form-hub-section"
    >
      <div className="container-x">
        {/* The intro block (eyebrow, main heading, subtitle, trust badges,
            pricing note) now lives in the Home hero's right half. This
            section only renders the service cards + help strip below. */}
        <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {loading && [0, 1, 2].map((i) => <SkeletonCard key={i} />)}
          {!loading && error && (
            <div className="col-span-full rounded-xl border border-rose-500/30 bg-rose-500/10 p-5 text-sm text-rose-200" data-testid="form-hub-error">
              {t("form_hub.load_error")}{" "}
              <a href={`https://wa.me/${WHATSAPP_NUMBER || "918055592955"}`} className="underline font-bold" target="_blank" rel="noopener noreferrer">
                +91 80555 92955
              </a>.
            </div>
          )}
          {!loading && !error && services.length === 0 && (
            <div className="col-span-full rounded-xl border border-white/10 bg-white/[0.03] p-6 text-center text-white/60 text-sm" data-testid="form-hub-empty">
              {t("form_hub.empty")}
            </div>
          )}
          {!loading && services.map((svc) => <ServiceCard key={svc.slug} svc={svc} />)}
        </div>

        <div className="mt-10 flex flex-wrap gap-3 items-center justify-between rounded-2xl border border-white/10 bg-white/[0.03] p-5">
          <div className="flex items-center gap-3">
            <div className="grid h-10 w-10 place-items-center rounded-full bg-emerald-500/15 border border-emerald-400/40">
              <MessageCircle className="h-4 w-4 text-emerald-300" />
            </div>
            <div>
              <div className="text-sm font-semibold text-white">{t("form_hub.help_headline")}</div>
              <div className="text-xs text-white/50">{t("form_hub.help_sub")}</div>
            </div>
          </div>
          <div className="flex gap-2">
            <a
              href={`https://wa.me/${WHATSAPP_NUMBER || "918055592955"}?text=${encodeURIComponent("Hi UID Infotech, I need help choosing the right form-filling service.")}`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 rounded-full bg-[#25D366] hover:bg-[#20bf5c] px-4 py-2 text-sm font-semibold text-white"
              data-testid="form-hub-cta-wa"
            >
              <MessageCircle className="h-4 w-4" /> {t("common.chat_now")}
            </a>
            <Link
              to="/track"
              className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/[0.04] hover:bg-white/10 px-4 py-2 text-sm font-semibold text-white/85"
              data-testid="form-hub-cta-track"
            >
              {t("common.track_order")} <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}
